import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  Switch,
  Image,
} from 'react-native';
import { getScaledValue } from '@rnv/renative';
import { useAuthStore } from '../../state/authStore';
import {
  contentApi,
  VideoListItem,
  VideoDetail,
} from '../../api/content';
import { apiRequest } from '../../api/client';
import { stubIcon } from '../../icons/stubLibrary';
import { ChevronLeft, Check, ArrowRight } from 'lucide-react-native';
import { authApi } from '../../api/auth';
import { CONTENT_CATEGORIES } from '../../data/categories';

// One shared vocabulary — see src/data/categories.ts. This list used to be a
// fourth, lowercase variant, so anything tagged from here was invisible to the
// case-sensitive category filters.
const CATEGORIES = CONTENT_CATEGORIES;

export default function ContentAdminScreen({ navigation }: { navigation: any }) {
  const { accessToken } = useAuthStore();
  const [videos, setVideos] = useState<VideoListItem[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<VideoDetail | null>(null);
  const [showAddVideo, setShowAddVideo] = useState(false);

  const [title, setTitle] = useState('');
  const [hlsUrl, setHlsUrl] = useState('');
  const [thumbUrl, setThumbUrl] = useState('');
  const [ageMin, setAgeMin] = useState('2');
  const [ageMax, setAgeMax] = useState('8');
  const [duration, setDuration] = useState('300');
  const [selectedCats, setSelectedCats] = useState<string[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  // This screen is a super-admin surface: it edits the GLOBAL catalog, and the
  // backend guards every write with SuperAdminGuard. MeScreen now only offers
  // the entry point to super-admins, but web routes by URL hash (#/ContentAdmin),
  // so the screen re-checks rather than trusting how it was reached.
  // `null` = still checking, so we don't flash the form then yank it away.
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!accessToken) { setAllowed(false); return; }
    let cancelled = false;
    authApi
      .me(accessToken)
      .then((p) => { if (!cancelled) setAllowed(p.isSuperAdmin === true); })
      // Fail closed: if we can't confirm, don't show the admin surface.
      .catch(() => { if (!cancelled) setAllowed(false); });
    return () => { cancelled = true; };
  }, [accessToken]);

  useEffect(() => {
    if (allowed) loadVideos();
  }, [allowed]);

  const loadVideos = async () => {
    if (!accessToken) return;
    const res = await contentApi.list({}, accessToken);
    setVideos(res.videos);
  };

  const loadVideoDetail = async (id: string) => {
    if (!accessToken) return;
    const detail = await contentApi.get(id, accessToken);
    setSelectedVideo(detail);
    setShowAddVideo(false);
  };

  const handleAddVideo = async () => {
    if (!title || !hlsUrl) { Alert.alert('Title and HLS URL are required'); return; }
    try {
      await apiRequest('/v1/admin/videos', {
        method: 'POST',
        body: {
          title,
          hlsUrl,
          thumbUrl: thumbUrl || 'https://picsum.photos/seed/' + title.replace(/ /g, '') + '/640/360',
          ageMin: parseInt(ageMin),
          ageMax: parseInt(ageMax),
          duration: parseInt(duration),
          categories: selectedCats,
          isPremium,
        },
        token: accessToken!,
      });
      setShowAddVideo(false);
      setTitle('');
      setHlsUrl('');
      setThumbUrl('');
      setSelectedCats([]);
      loadVideos();
    } catch (e: unknown) {
      Alert.alert('Failed', e instanceof Error ? e.message : 'Unknown error');
    }
  };

  const toggleCat = (cat: string) =>
    setSelectedCats((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );

  if (allowed !== true) {
    return (
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.back}>
          <ChevronLeft size={20} color="#333" strokeWidth={2.2} />
        </TouchableOpacity>
        <Text style={styles.title}>Content Manager</Text>
        <Text style={styles.label}>
          {allowed === null
            ? 'Checking your access…'
            : "This area is for Kidly staff. To add your own videos, use Studio."}
        </Text>
      </ScrollView>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <TouchableOpacity onPress={() => navigation.goBack()} style={styles.back}>
        <ChevronLeft size={20} color="#333" strokeWidth={2.2} />
      </TouchableOpacity>
      <Text style={styles.title}>Content Manager</Text>

      <TouchableOpacity
        style={styles.addBtn}
        onPress={() => { setShowAddVideo(true); setSelectedVideo(null); }}
      >
        <Text style={styles.addBtnText}>+ Add Video</Text>
      </TouchableOpacity>

      {showAddVideo && (
        <View style={styles.form}>
          <Text style={styles.formTitle}>New Video</Text>
          <TextInput style={styles.input} placeholder="Title" placeholderTextColor="#aaa" value={title} onChangeText={setTitle} />
          <TextInput style={styles.input} placeholder="HLS URL" placeholderTextColor="#aaa" value={hlsUrl} onChangeText={setHlsUrl} />
          <TextInput style={styles.input} placeholder="Thumbnail URL (optional)" placeholderTextColor="#aaa" value={thumbUrl} onChangeText={setThumbUrl} />
          <View style={styles.row}>
            <TextInput style={[styles.input, styles.halfInput]} placeholder="Age min" placeholderTextColor="#aaa" keyboardType="numeric" value={ageMin} onChangeText={setAgeMin} />
            <TextInput style={[styles.input, styles.halfInput]} placeholder="Age max" placeholderTextColor="#aaa" keyboardType="numeric" value={ageMax} onChangeText={setAgeMax} />
          </View>
          <TextInput style={styles.input} placeholder="Duration (seconds)" placeholderTextColor="#aaa" keyboardType="numeric" value={duration} onChangeText={setDuration} />
          <View style={styles.row}>
            <Text style={styles.label}>Premium</Text>
            <Switch value={isPremium} onValueChange={setIsPremium} trackColor={{ true: '#FF8C42' }} />
          </View>
          <View style={styles.tags}>
            {CATEGORIES.map((cat) => (
              <TouchableOpacity
                key={cat}
                style={[styles.tag, selectedCats.includes(cat) && styles.tagActive]}
                onPress={() => toggleCat(cat)}
              >
                <Text style={[styles.tagText, selectedCats.includes(cat) && styles.tagTextActive]}>{cat}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity style={styles.saveBtn} onPress={handleAddVideo}>
            <Text style={styles.saveBtnText}>Save Video</Text>
          </TouchableOpacity>
        </View>
      )}

      <Text style={styles.sectionTitle}>Videos ({videos.length})</Text>
      {videos.map((v) => (
        <TouchableOpacity
          key={v.id}
          style={[styles.videoCard, selectedVideo?.id === v.id && styles.videoCardActive]}
          onPress={() => loadVideoDetail(v.id)}
        >
          <Text style={styles.videoTitle}>{v.title}</Text>
          <Text style={styles.videoMeta}>
            Age {v.ageMin}-{v.ageMax} | {v.categories.join(', ')} | {v.isPremium ? 'Premium' : 'Free'}
          </Text>
        </TouchableOpacity>
      ))}

      {selectedVideo && (
        <View style={styles.detail}>
          <Text style={styles.detailTitle}>{selectedVideo.title} — Questions</Text>
          {selectedVideo.questions.length === 0 && (
            <Text style={styles.empty}>No questions yet</Text>
          )}
          {selectedVideo.questions.map((q) => {
            // Legacy rows are `string[]`, new ones are `{label, iconKey?}[]`,
            // and ancient ones may even arrive as a JSON-encoded string.
            const raw: unknown = Array.isArray(q.options)
              ? q.options
              : typeof q.options === 'string'
                ? (() => {
                    try {
                      return JSON.parse(q.options as unknown as string);
                    } catch {
                      return [];
                    }
                  })()
                : [];
            const opts = (Array.isArray(raw) ? raw : []).map(
              (o: unknown): { label: string; iconKey?: string } => {
                if (typeof o === 'string') return { label: o };
                if (o && typeof o === 'object' && 'label' in o) {
                  const obj = o as { label?: unknown; iconKey?: unknown };
                  return {
                    label: typeof obj.label === 'string' ? obj.label : '',
                    iconKey:
                      typeof obj.iconKey === 'string' ? obj.iconKey : undefined,
                  };
                }
                return { label: String(o) };
              },
            );
            return (
              <View key={q.id} style={styles.questionCard}>
                <Text style={styles.qTime}>@ {q.tSec}s</Text>
                <Text style={styles.qPrompt}>{q.prompt}</Text>
                {opts.map((opt, i) => {
                  const icon = stubIcon(opt.iconKey);
                  return (
                    <View key={i} style={styles.qOptionRow}>
                      {icon ? (
                        <Image
                          source={{ uri: icon.imageUrl }}
                          style={styles.qOptionIcon}
                          resizeMode="contain"
                        />
                      ) : null}
                      <Text
                        style={[
                          styles.qOption,
                          i === q.correct && styles.qOptionCorrect,
                        ]}
                      >
                        {i === q.correct ? <Check size={14} color="#2a7a2a" strokeWidth={2.5} /> : null}{' '}
                        {opt.label}
                      </Text>
                    </View>
                  );
                })}
              </View>
            );
          })}
          {/* All question authoring (add / edit / delete, image options,
              corners layout, timestamp control) lives in the dedicated
              ParentQuestionsScreen so this admin surface stays focused on
              video CRUD. The inline mini-form here used to only support
              "add" — replacing it with a single jump keeps parity with
              the web (which routes to a full editor too). */}
          <TouchableOpacity
            style={styles.addQuestionBtn}
            onPress={() =>
              navigation.navigate('ParentQuestions', {
                videoId: selectedVideo.id,
                videoTitle: selectedVideo.title,
                videoDurationSec: selectedVideo.duration,
                videoUrl: selectedVideo.hlsUrl,
              })
            }
          >
            <Text style={styles.addBtnText}>Manage Questions </Text><ArrowRight size={16} color="#fff" strokeWidth={2} />
          </TouchableOpacity>
        </View>
      )}
    </ScrollView>
  );
}

const s = getScaledValue;
const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#FFF8F0' },
  content: { padding: s(24) },
  back: { marginBottom: s(16) },
  backText: { color: '#FF8C42', fontSize: s(15) },
  title: { fontSize: s(22), fontWeight: 'bold', color: '#333', marginBottom: s(16) },
  addBtn: { backgroundColor: '#FF8C42', borderRadius: s(10), padding: s(14), alignItems: 'center', marginBottom: s(20) },
  addBtnText: { color: '#fff', fontSize: s(15), fontWeight: 'bold' },
  form: { backgroundColor: '#fff', borderRadius: s(12), padding: s(20), marginBottom: s(20), borderWidth: 1, borderColor: '#eee' },
  formTitle: { fontSize: s(17), fontWeight: 'bold', color: '#333', marginBottom: s(14) },
  input: { borderWidth: 1, borderColor: '#ddd', borderRadius: s(8), padding: s(12), fontSize: s(15), backgroundColor: '#fafafa', marginBottom: s(10) },
  halfInput: { flex: 1, marginHorizontal: s(4) },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: s(10), gap: s(8) },
  label: { fontSize: s(14), color: '#444', fontWeight: '600' },
  tags: { flexDirection: 'row', flexWrap: 'wrap', gap: s(6), marginBottom: s(14) },
  tag: { paddingHorizontal: s(12), paddingVertical: s(6), borderRadius: s(16), backgroundColor: '#f0f0f0', borderWidth: 1, borderColor: '#ddd' },
  tagActive: { backgroundColor: '#FF8C42', borderColor: '#FF8C42' },
  tagText: { fontSize: s(12), color: '#666' },
  tagTextActive: { color: '#fff', fontWeight: '600' },
  saveBtn: { backgroundColor: '#FF8C42', borderRadius: s(10), padding: s(14), alignItems: 'center', marginTop: s(8) },
  saveBtnText: { color: '#fff', fontSize: s(15), fontWeight: 'bold' },
  sectionTitle: { fontSize: s(17), fontWeight: 'bold', color: '#333', marginBottom: s(12), marginTop: s(8) },
  videoCard: { backgroundColor: '#fff', borderRadius: s(10), padding: s(14), marginBottom: s(8), borderWidth: 1, borderColor: '#eee' },
  videoCardActive: { borderColor: '#FF8C42', backgroundColor: '#FFF3E0' },
  videoTitle: { fontSize: s(15), fontWeight: '600', color: '#333' },
  videoMeta: { fontSize: s(12), color: '#888', marginTop: s(4) },
  detail: { backgroundColor: '#fff', borderRadius: s(12), padding: s(20), marginTop: s(16), borderWidth: 1, borderColor: '#eee' },
  detailTitle: { fontSize: s(16), fontWeight: 'bold', color: '#333', marginBottom: s(12) },
  empty: { color: '#aaa', fontSize: s(14), marginBottom: s(12) },
  questionCard: { backgroundColor: '#fafafa', borderRadius: s(8), padding: s(12), marginBottom: s(10), borderWidth: 1, borderColor: '#eee' },
  qTime: { fontSize: s(12), color: '#FF8C42', fontWeight: 'bold', marginBottom: s(4) },
  qPrompt: { fontSize: s(14), fontWeight: '600', color: '#333', marginBottom: s(8) },
  qOption: { fontSize: s(13), color: '#555', paddingVertical: s(2) },
  qOptionCorrect: { color: '#4CAF50', fontWeight: 'bold' },
  qOptionRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: s(2) },
  qOptionIcon: { width: s(20), height: s(20), marginRight: s(6) },
  addQuestionBtn: { backgroundColor: '#FFF3E0', borderRadius: s(10), padding: s(12), alignItems: 'center', marginTop: s(8), borderWidth: 1, borderColor: '#FF8C42' },
});
